import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  TrophyIcon, 
  PlusIcon, 
  CalendarIcon,
  MapPinIcon,
  UsersIcon,
  DocumentArrowDownIcon,
  LockClosedIcon,
  PencilIcon,
  XMarkIcon,
  TrashIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { toProperCase } from '../../utils/formatUtils';

const AdminCompetitions = () => {
  const { currentUser } = useAuth();
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingCompetition, setEditingCompetition] = useState(null);
  const [showRegistrationsModal, setShowRegistrationsModal] = useState(false);
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    start_date: '',
    end_date: '',
    registration_deadline: '',
    location: '',
    max_participants: '',
    free_registration: false,  // Allow free registration option
    events: [],
    division_mappings: {}
  });

  // SAA default division mapping (same as backend)
  const SAA_DIVISION_MAPPINGS_DEFAULT = {
    'U15 (13 - 14 years old)': '1',
    'U18 (15 - 17 years old)': '2',
    'U20 (18 - 19 years old)': '3',
    'Open (16 years and above)': '4',
    'A Div (17 - 19 years old)': '5',
    'Intermediate (Vertical Jumps Only)': '6',
    'Advance (Vertical Jumps Only)': '7',
    'Novice (Vertical Jumps Only)': '8'
  };

  const [availableEvents, setAvailableEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [newEvent, setNewEvent] = useState({
    event_code: '',
    event_name: '',
    gender: 'M',
    age_groups: [],
    entry_fee: '15', // Default entry fee
    divisions: ['Opens', 'U20', 'U18', 'U15', 'Novice', 'Intermediate', 'Advance', 'A Division'] // Default: all divisions
  });
  const [divisionMapping, setDivisionMapping] = useState({
    division_type: '',
    event_division: ''
  });

  const deleteCompetition = async (competitionId) => {
    if (!window.confirm('Are you sure you want to delete this competition? This action cannot be undone.')) {
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      const response = await fetch(`${apiUrl}/api/admin/competitions/${competitionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok) {
        // Remove competition from state
        setCompetitions(prev => prev.filter(comp => comp.id !== competitionId));
        alert('Competition deleted successfully!');
      } else {
        alert(data.error || 'Failed to delete competition');
      }
    } catch (error) {
      console.error('Error deleting competition:', error);
      alert('Failed to delete competition. Please try again.');
    }
  };

  const fetchCompetitions = useCallback(async () => {
    try {
      console.log('Fetching competitions...');
      const token = await currentUser.getIdToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      const response = await fetch(`${apiUrl}/api/competitions/list`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch competitions');
      }

      const data = await response.json();
      console.log('Competitions data:', data);
      // Use backend sorting (by created_date, newest first)
      console.log('Competitions sorted by created_date:', data.competitions);
      setCompetitions(data.competitions || []);
    } catch (error) {
      console.error('Error fetching competitions:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  const fetchAvailableEvents = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      const response = await fetch(`${apiUrl}/api/competitions/events`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch events: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      // Sort events alphabetically by event name
      const sortedEvents = (data.events || []).sort((a, b) => {
        const eventA = a.event || a.event_name || '';
        const eventB = b.event || b.event_name || '';
        return eventA.localeCompare(eventB);
      });
      setAvailableEvents(sortedEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  }, []);

  useEffect(() => {
    if (currentUser) {
      fetchCompetitions();
    }
    fetchAvailableEvents();
  }, [currentUser, fetchCompetitions, fetchAvailableEvents]);

  // Prevent background scrolling when modals are open
  useEffect(() => {
    if (showCreateModal || showEditModal || showRegistrationsModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to reset overflow when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showCreateModal, showEditModal, showRegistrationsModal]);

  const handleCreateCompetition = async (e) => {
    e.preventDefault();
    console.log('Creating competition...', formData);
    
    try {
      const token = await currentUser.getIdToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      console.log('API URL:', `${apiUrl}/api/competitions/create`);
      const response = await fetch(`${apiUrl}/api/competitions/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();
      console.log('Response:', result);
      
      if (!response.ok) {
        console.error('API Error:', result);
        throw new Error(result.error || 'Failed to create competition');
      }

      console.log('Competition created successfully!');
      setShowCreateModal(false);
      setFormData({
        name: '',
        description: '',
        start_date: '',
        end_date: '',
        registration_deadline: '',
        location: '',
        max_participants: '',
        free_registration: false,
        events: [],
        division_mappings: {}
      });
      fetchCompetitions();
    } catch (error) {
      console.error('Error creating competition:', error);
      alert('Failed to create competition');
    }
  };

  const handleCloseRegistration = async (competitionId) => {
    if (!window.confirm('Are you sure you want to close registration for this competition?')) {
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      const response = await fetch(`${apiUrl}/api/competitions/${competitionId}/close-registration`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to close registration');
      }

      fetchCompetitions();
    } catch (error) {
      console.error('Error closing registration:', error);
      alert('Failed to close registration');
    }
  };

  const handleExportData = async (competitionId) => {
    try {
      const token = await currentUser.getIdToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      const response = await fetch(`${apiUrl}/api/competitions/${competitionId}/export`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to export data');
      }

      // Get the filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition 
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '') 
        : `competition_${competitionId}_export.xlsx`;

      // Create blob from response and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Failed to export data');
    }
  };

  const handleApproveRegistration = async (registrationId, userName) => {
    if (!window.confirm(`Are you sure you want to approve the registration for ${userName}? This will mark their registration as confirmed without requiring payment.`)) {
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      const response = await fetch(`${apiUrl}/api/competitions/registrations/${registrationId}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (response.ok) {
        // Refresh the registrations list
        if (selectedCompetition) {
          handleViewRegistrations(selectedCompetition);
        }
        alert(data.message || 'Registration approved successfully!');
      } else {
        alert(data.error || 'Failed to approve registration');
      }
    } catch (error) {
      console.error('Error approving registration:', error);
      alert('Failed to approve registration. Please try again.');
    }
  };

  const handleRejectRegistration = async (registrationId, userName) => {
    if (!window.confirm(`Are you sure you want to reject the registration for ${userName}? This cannot be undone.`)) {
      return;
    }

    try {
      const token = await currentUser.getIdToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      const response = await fetch(`${apiUrl}/api/competitions/registrations/${registrationId}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      if (response.ok) {
        if (selectedCompetition) {
          handleViewRegistrations(selectedCompetition);
        }
        alert(data.message || 'Registration rejected.');
      } else {
        alert(data.error || 'Failed to reject registration');
      }
    } catch (error) {
      console.error('Error rejecting registration:', error);
      alert('Failed to reject registration. Please try again.');
    }
  };

  const handleEditCompetition = (competition) => {
    console.log('Editing competition:', competition);
    setEditingCompetition(competition);
    setFormData({
      name: competition.name || '',
      description: competition.description || '',
      start_date: competition.start_date || '',
      end_date: competition.end_date || '',
      registration_deadline: competition.registration_deadline || '',
      location: competition.location || '',
      max_participants: competition.max_participants || '',
      free_registration: competition.free_registration || false,
      events: competition.events || [],
      division_mappings: (competition.division_mappings && Object.keys(competition.division_mappings).length > 0)
        ? { ...competition.division_mappings }
        : { ...SAA_DIVISION_MAPPINGS_DEFAULT }
    });
    setShowEditModal(true);
  };

  const handleOpenCreateModal = () => {
    // Reset form data; use SAA default division mapping
    setFormData({
      name: '',
      description: '',
      start_date: '',
      end_date: '',
      registration_deadline: '',
      location: '',
      max_participants: '',
      free_registration: false,
      events: [],
      division_mappings: { ...SAA_DIVISION_MAPPINGS_DEFAULT }
    });
    // Reset other state variables
    setSelectedEventId('');
    setNewEvent({
      event_code: '',
      event_name: '',
      gender: 'M',
      age_groups: [],
      entry_fee: '15',
      divisions: ['Opens', 'U20', 'U18', 'U15', 'Novice', 'Intermediate', 'Advance', 'A Division']
    });
    setDivisionMapping({
      division_type: '',
      event_division: ''
    });
    setShowCreateModal(true);
  };

  const handleViewRegistrations = async (competition) => {
    try {
      const token = await currentUser.getIdToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      const response = await fetch(`${apiUrl}/api/competitions/${competition.id}/registrations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch registrations');
      }

      const data = await response.json();
      setRegistrations(data.registrations || []);
      setSelectedCompetition(competition);
      setShowRegistrationsModal(true);
    } catch (error) {
      console.error('Error fetching registrations:', error);
      alert('Failed to fetch registrations');
    }
  };

  const handleUpdateCompetition = async (e) => {
    e.preventDefault();
    console.log('Updating competition...', formData);
    
    try {
      const token = await currentUser.getIdToken();
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      const response = await fetch(`${apiUrl}/api/competitions/${editingCompetition.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();
      console.log('Update Response:', result);
      
      if (!response.ok) {
        console.error('API Error:', result);
        throw new Error(result.error || 'Failed to update competition');
      }

      console.log('Competition updated successfully!');
      setShowEditModal(false);
      setEditingCompetition(null);
      fetchCompetitions();
    } catch (error) {
      console.error('Error updating competition:', error);
      alert('Failed to update competition');
    }
  };

  const addEvent = () => {
    if (!selectedEventId || !newEvent.entry_fee) {
      alert('Please select an event and enter entry fee');
      return;
    }

    const selectedEvent = availableEvents.find(event => event.event === selectedEventId);
    if (!selectedEvent) {
      alert('Selected event not found');
      return;
    }

    // Check for duplicate events (check if both M and F versions already exist)
    const hasMaleVersion = formData.events.some(event => 
      event.event_name === selectedEvent.event && event.gender === 'M'
    );
    const hasFemaleVersion = formData.events.some(event => 
      event.event_name === selectedEvent.event && event.gender === 'F'
    );
    
    if (hasMaleVersion && hasFemaleVersion) {
      alert('This event is already added for both genders');
      return;
    }

    // Create events for both genders automatically
    const eventsToAdd = [];
    const entryFee = parseFloat(newEvent.entry_fee) || (formData.free_registration ? 0 : 15);
    const divisions = newEvent.divisions || ['Opens', 'U20', 'U18', 'U15', 'Novice', 'Intermediate', 'Advance', 'A Division'];

    // Add Male version if not already exists
    if (!hasMaleVersion) {
      eventsToAdd.push({
        event_code: selectedEvent.event_code,
        event_name: selectedEvent.event,
        gender: 'M',
        type: selectedEvent.type,
        entry_fee: entryFee,
        divisions: divisions
      });
    }

    // Add Female version if not already exists
    if (!hasFemaleVersion) {
      eventsToAdd.push({
        event_code: selectedEvent.event_code,
        event_name: selectedEvent.event,
        gender: 'F',
        type: selectedEvent.type,
        entry_fee: entryFee,
        divisions: divisions
      });
    }
    
    setFormData({
      ...formData,
      events: [...formData.events, ...eventsToAdd]
    });
    
    setNewEvent({
      event_code: '',
      event_name: '',
      gender: 'M',
      age_groups: [],
      entry_fee: formData.free_registration ? '0' : '15',
      divisions: ['Opens', 'U20', 'U18', 'U15', 'Novice', 'Intermediate', 'Advance', 'A Division']
    });
    setSelectedEventId('');
  };

  const addDivisionMapping = () => {
    if (divisionMapping.division_type && divisionMapping.event_division) {
      setFormData({
        ...formData,
        division_mappings: {
          ...formData.division_mappings,
          [divisionMapping.division_type]: divisionMapping.event_division
        }
      });
      setDivisionMapping({
        division_type: '',
        event_division: ''
      });
    }
  };

  const removeDivisionMapping = (divisionType) => {
    const newMappings = { ...formData.division_mappings };
    delete newMappings[divisionType];
    setFormData({
      ...formData,
      division_mappings: newMappings
    });
  };

  const removeEvent = (index) => {
    const updatedEvents = formData.events.filter((_, i) => i !== index);
    setFormData({ ...formData, events: updatedEvents });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Competition Management</h1>
              <p className="mt-2 text-gray-600">Create and manage athletic competitions</p>
            </div>
            <button
              onClick={handleOpenCreateModal}
              className="bg-oldham-gold text-gray-900 px-4 py-2 rounded-lg hover:bg-oldham-gold/90 transition-colors flex items-center gap-2"
            >
              <PlusIcon className="h-5 w-5" />
              Create Competition
            </button>
          </div>
        </div>

        {/* Competitions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {competitions.map((competition) => (
            <div key={competition.id} className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col h-full">
              {/* Card Header */}
              <div className="p-6 pb-4">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">{competition.name}</h3>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ml-2 ${getStatusColor(competition.status || 'unknown')}`}>
                    {(competition.status || 'unknown').toUpperCase()}
                  </span>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center text-gray-600">
                    <CalendarIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                    <span className="text-sm">
                      {competition.start_date ? new Date(competition.start_date).toLocaleDateString() : 'TBD'} - {competition.end_date ? new Date(competition.end_date).toLocaleDateString() : 'TBD'}
                    </span>
                  </div>
                  
                  <div className="flex items-center text-gray-600">
                    <MapPinIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                    <span className="text-sm truncate">{competition.location || 'Location TBD'}</span>
                  </div>
                  
                  <div className="flex items-center text-gray-600">
                    <UsersIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                    <span className="text-sm">{competition.registration_count || 0} registered</span>
                  </div>
                </div>
              </div>

              {/* Card Footer - Buttons aligned to bottom */}
              <div className="mt-auto p-6 pt-4 border-t border-gray-100">
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => handleEditCompetition(competition)}
                    className="bg-green-600 text-white px-3 py-2 rounded text-sm hover:bg-green-700 transition-colors flex items-center justify-center gap-1"
                  >
                    <PencilIcon className="h-4 w-4" />
                    Edit
                  </button>
                  
                  <button
                    onClick={() => handleViewRegistrations(competition)}
                    className="bg-purple-600 text-white px-3 py-2 rounded text-sm hover:bg-purple-700 transition-colors flex items-center justify-center gap-1"
                  >
                    <UsersIcon className="h-4 w-4" />
                    View
                  </button>
                </div>
                
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <button
                    onClick={() => handleExportData(competition.id)}
                    className="bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700 transition-colors flex items-center justify-center gap-1"
                  >
                    <DocumentArrowDownIcon className="h-4 w-4" />
                    Export
                  </button>
                  
                  <button
                    onClick={() => deleteCompetition(competition.id)}
                    className="bg-red-600 text-white px-3 py-2 rounded text-sm hover:bg-red-700 transition-colors flex items-center justify-center gap-1"
                  >
                    <TrashIcon className="h-4 w-4" />
                    Delete
                  </button>
                </div>
                
                {competition.status === 'open' && (
                  <div className="mt-2">
                    <button
                      onClick={() => handleCloseRegistration(competition.id)}
                      className="w-full bg-yellow-600 text-white px-3 py-2 rounded text-sm hover:bg-yellow-700 transition-colors flex items-center justify-center gap-1"
                    >
                      <LockClosedIcon className="h-4 w-4" />
                      Close Registration
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {competitions.length === 0 && (
          <div className="text-center py-12">
            <TrophyIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No competitions</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by creating a new competition.</p>
          </div>
        )}
      </div>

      {/* Create Competition Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Competition</h3>
              
              <form onSubmit={handleCreateCompetition} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Competition Name</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-oldham-gold focus:border-oldham-gold"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Location</label>
                    <input
                      type="text"
                      required
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-oldham-gold focus:border-oldham-gold"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-oldham-gold focus:border-oldham-gold"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Start Date</label>
                    <input
                      type="date"
                      required
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-oldham-gold focus:border-oldham-gold"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">End Date</label>
                    <input
                      type="date"
                      required
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-oldham-gold focus:border-oldham-gold"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Registration Deadline</label>
                    <input
                      type="date"
                      required
                      value={formData.registration_deadline}
                      onChange={(e) => setFormData({ ...formData, registration_deadline: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-oldham-gold focus:border-oldham-gold"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Max Participants</label>
                  <input
                    type="number"
                    value={formData.max_participants}
                    onChange={(e) => setFormData({ ...formData, max_participants: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-oldham-gold focus:border-oldham-gold"
                  />
                </div>

                {/* Free Registration Toggle */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="free_registration"
                    checked={formData.free_registration}
                    onChange={(e) => {
                      const isFree = e.target.checked;
                      setFormData({ 
                        ...formData, 
                        free_registration: isFree 
                      });
                      // Update newEvent entry fee immediately
                      setNewEvent({
                        ...newEvent,
                        entry_fee: isFree ? '0' : '15'
                      });
                    }}
                    className="h-4 w-4 text-oldham-gold focus:ring-oldham-gold border-gray-300 rounded"
                  />
                  <label htmlFor="free_registration" className="ml-2 block text-sm font-medium text-gray-700">
                    Allow free registration (no entry fees)
                  </label>
                </div>
                <p className="text-xs text-gray-500 -mt-1">
                  When enabled, all events in this competition will have $0 entry fee by default
                </p>

                {/* Division Mappings Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Division Mappings
                  </label>
                  <p className="text-sm text-gray-500 mb-3">
                    Map Division Types to Event Division numbers for Excel export
                  </p>
                  
                  <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
                    {Object.entries(formData.division_mappings).map(([divisionType, eventDivision]) => (
                      <div key={divisionType} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg gap-2">
                        <span className="font-medium break-words flex-1 min-w-0">{divisionType} → {eventDivision}</span>
                        <button
                          type="button"
                          onClick={() => removeDivisionMapping(divisionType)}
                          className="text-red-600 hover:text-red-800 text-sm flex-shrink-0 px-2 py-1 hover:bg-red-50 rounded"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                    
                    <div className="flex flex-col sm:flex-row gap-2">
                      <select
                        value={divisionMapping.division_type}
                        onChange={(e) => setDivisionMapping({ ...divisionMapping, division_type: e.target.value })}
                        className="flex-1 min-w-0 border-gray-300 rounded-md shadow-sm focus:ring-oldham-gold focus:border-oldham-gold text-sm"
                      >
                        <option value="">Select Division Type</option>
                        <option value="Opens">Opens</option>
                        <option value="U20">U20 (Under 20)</option>
                        <option value="U18">U18 (Under 18)</option>
                        <option value="U15">U15 (Under 15)</option>
                        <option value="Novice">Novice</option>
                        <option value="Intermediate">Intermediate</option>
                        <option value="Advance">Advance</option>
                        <option value="A Division">A Division</option>
                      </select>
                      <span className="hidden sm:flex items-center text-gray-500 flex-shrink-0">→</span>
                      <select
                        value={divisionMapping.event_division}
                        onChange={(e) => setDivisionMapping({ ...divisionMapping, event_division: e.target.value })}
                        className="flex-1 min-w-0 border-gray-300 rounded-md shadow-sm focus:ring-oldham-gold focus:border-oldham-gold text-sm"
                      >
                        <option value="">Select Event Division</option>
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                        <option value="4">4</option>
                        <option value="5">5</option>
                        <option value="6">6</option>
                        <option value="7">7</option>
                        <option value="8">8</option>
                      </select>
                      <button
                        type="button"
                        onClick={addDivisionMapping}
                        disabled={!divisionMapping.division_type || !divisionMapping.event_division}
                        className="bg-oldham-gold text-gray-900 px-3 py-2 rounded text-sm hover:bg-oldham-gold/90 flex-shrink-0 disabled:bg-gray-300 disabled:cursor-not-allowed"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                </div>

                {/* Events Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Events</label>
                  
                  {/* Bulk Event Selection */}
                  <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-sm font-medium text-blue-800">Quick Add Multiple Events</h4>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            // Select all track events
                            const trackEvents = Array.from(new Set(availableEvents.filter(event => event.type === 'track').map(e => e.event)));
                            const fieldEvents = Array.from(new Set(availableEvents.filter(event => event.type === 'field').map(e => e.event)));
                            const allEvents = [...trackEvents, ...fieldEvents];
                            
                            const eventsToAdd = allEvents.flatMap(eventName => {
                              return ['M', 'F'].map(gender => {
                                const eventDetails = availableEvents.find(e => e.event === eventName);
                                return {
                                  event_code: eventDetails?.event_code || '',
                                  event_name: eventName,
                                  gender: gender,
                                  type: eventDetails?.type || 'track',
                                  entry_fee: formData.free_registration ? 0 : 15,
                                  divisions: ['Opens', 'U20', 'U18', 'U15', 'Novice', 'Intermediate', 'Advance', 'A Division']
                                };
                              });
                            });
                            
                            setFormData({
                              ...formData,
                              events: eventsToAdd
                            });
                          }}
                          className="bg-blue-600 text-white px-3 py-1 rounded text-xs hover:bg-blue-700 transition-colors"
                        >
                          Select All Events
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setFormData({
                              ...formData,
                              events: []
                            });
                          }}
                          className="bg-gray-600 text-white px-3 py-1 rounded text-xs hover:bg-gray-700 transition-colors"
                        >
                          Clear All
                        </button>
                      </div>
                    </div>
                          <p className="text-xs text-blue-600 mb-3">
                            ✓ Events are automatically added for both Male and Female categories
                          </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-blue-700 mb-2">Track Events</label>
                        <div className="space-y-2 max-h-32 overflow-y-auto">
                          {/* Group track events by name, show only unique event names */}
                          {Array.from(new Set(availableEvents.filter(event => event.type === 'track').map(e => e.event)))
                            .sort()
                            .map((eventName) => {
                              // Check if both M and F versions are added
                              const maleAdded = formData.events.some(e => e.event_name === eventName && e.gender === 'M');
                              const femaleAdded = formData.events.some(e => e.event_name === eventName && e.gender === 'F');
                              const isFullyAdded = maleAdded && femaleAdded;
                              
                              // Get the event details (use male version as reference)
                              const eventDetails = availableEvents.find(e => e.event === eventName && e.type === 'track');
                              
                              return (
                                <label key={eventName} className="flex items-center space-x-2 text-sm">
                                  <input
                                    type="checkbox"
                                    checked={isFullyAdded}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        // Add both Male and Female versions
                                        const eventsToAdd = ['M', 'F'].map(gender => {
                                          const genderEvent = availableEvents.find(ev => 
                                            ev.event === eventName && ev.gender === gender && ev.type === 'track'
                                          );
                                          return {
                                            event_code: genderEvent?.event_code || eventDetails.event_code,
                                            event_name: eventName,
                                            gender: gender,
                                            type: 'track',
                                            entry_fee: formData.free_registration ? 0 : 15,
                                            divisions: ['Opens', 'U20', 'U18', 'U15', 'Novice', 'Intermediate', 'Advance', 'A Division']
                                          };
                                        });
                                        
                                        setFormData({
                                          ...formData,
                                          events: [...formData.events, ...eventsToAdd]
                                        });
                                      } else {
                                        // Remove both Male and Female versions
                                        const updatedEvents = formData.events.filter(e => 
                                          e.event_name !== eventName
                                        );
                                        setFormData({ ...formData, events: updatedEvents });
                                      }
                                    }}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                  />
                                  <span className={`${isFullyAdded ? 'text-blue-800 font-medium' : 'text-blue-700'}`}>
                                    {eventName} <span className="text-xs text-gray-500">(M & F)</span>
                                  </span>
                                </label>
                              );
                            })}
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-blue-700 mb-2">Field Events</label>
                        <div className="space-y-2 max-h-32 overflow-y-auto">
                          {/* Group field events by name, show only unique event names */}
                          {Array.from(new Set(availableEvents.filter(event => event.type === 'field').map(e => e.event)))
                            .sort()
                            .map((eventName) => {
                              // Check if both M and F versions are added
                              const maleAdded = formData.events.some(e => e.event_name === eventName && e.gender === 'M');
                              const femaleAdded = formData.events.some(e => e.event_name === eventName && e.gender === 'F');
                              const isFullyAdded = maleAdded && femaleAdded;
                              
                              // Get the event details (use male version as reference)
                              const eventDetails = availableEvents.find(e => e.event === eventName && e.type === 'field');
                              
                              return (
                                <label key={eventName} className="flex items-center space-x-2 text-sm">
                                  <input
                                    type="checkbox"
                                    checked={isFullyAdded}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        // Add both Male and Female versions
                                        const eventsToAdd = ['M', 'F'].map(gender => {
                                          const genderEvent = availableEvents.find(ev => 
                                            ev.event === eventName && ev.gender === gender && ev.type === 'field'
                                          );
                                          return {
                                            event_code: genderEvent?.event_code || eventDetails.event_code,
                                            event_name: eventName,
                                            gender: gender,
                                            type: 'field',
                                            entry_fee: formData.free_registration ? 0 : 15,
                                            divisions: ['Opens', 'U20', 'U18', 'U15', 'Novice', 'Intermediate', 'Advance', 'A Division']
                                          };
                                        });
                                        
                                        setFormData({
                                          ...formData,
                                          events: [...formData.events, ...eventsToAdd]
                                        });
                                      } else {
                                        // Remove both Male and Female versions
                                        const updatedEvents = formData.events.filter(e => 
                                          e.event_name !== eventName
                                        );
                                        setFormData({ ...formData, events: updatedEvents });
                                      }
                                    }}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                  />
                                  <span className={`${isFullyAdded ? 'text-blue-800 font-medium' : 'text-blue-700'}`}>
                                    {eventName} <span className="text-xs text-gray-500">(M & F)</span>
                                  </span>
                                </label>
                              );
                            })}
                        </div>
                      </div>
                    </div>
                          <p className="text-xs text-blue-600 mt-2">
                            Tip: Each event will be added for both Male and Female categories automatically. Entry fee: ${formData.free_registration ? '0' : '15'} per event.
                          </p>
                  </div>

                  {/* Individual Event Addition (Fallback) */}
                  <div className="bg-gray-50 p-4 rounded-md mb-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Add Individual Event</h4>
                    <p className="text-xs text-gray-600 mb-3">
                      ✓ Selecting an event will automatically add it for both Male and Female categories
                    </p>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Select Event</label>
                        <select
                          value={selectedEventId}
                          onChange={(e) => setSelectedEventId(e.target.value)}
                          className="w-full border-gray-300 rounded-md shadow-sm focus:ring-oldham-gold focus:border-oldham-gold text-sm"
                        >
                          <option value="">
                            {availableEvents.length > 0 ? 'Select Event' : 'Loading events...'}
                          </option>
                          {Array.from(new Set(availableEvents.map(event => event.event))).map((eventName) => {
                            const eventDetails = availableEvents.find(e => e.event === eventName);
                            return (
                              <option key={eventName} value={eventName}>
                                {eventName} - {eventDetails?.type}
                              </option>
                            );
                          })}
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Entry Fee (Price per Event)
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <span className="text-gray-500 text-sm">$</span>
                          </div>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder={formData.free_registration ? '0.00' : '15.00'}
                            value={newEvent.entry_fee}
                            onChange={(e) => setNewEvent({ ...newEvent, entry_fee: e.target.value })}
                            className="w-full pl-7 border-gray-300 rounded-md shadow-sm focus:ring-oldham-gold focus:border-oldham-gold text-sm"
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          {formData.free_registration ? 'Free competition - set to $0' : 'Standard fee is $15 per event'}
                        </p>
                      </div>

                      {/* Division Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Available Divisions (Age Groups)
                        </label>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {[
                            { code: 'Opens', name: 'Opens', age: 'No limit' },
                            { code: 'U20', name: 'Under 20', age: '16-19' },
                            { code: 'U18', name: 'Under 18', age: '14-17' },
                            { code: 'U15', name: 'Under 15', age: '12-14' },
                            { code: 'Novice', name: 'Novice', age: 'Beginner' },
                            { code: 'Intermediate', name: 'Intermediate', age: 'Intermediate' },
                            { code: 'Advance', name: 'Advance', age: 'Advanced' },
                            { code: 'A Division', name: 'A Division', age: '17-19' }
                          ].map((division) => (
                            <label key={division.code} className="flex items-center space-x-2 text-sm cursor-pointer">
                              <input
                                type="checkbox"
                                checked={newEvent.divisions?.includes(division.code)}
                                onChange={(e) => {
                                  const currentDivisions = newEvent.divisions || [];
                                  if (e.target.checked) {
                                    setNewEvent({ ...newEvent, divisions: [...currentDivisions, division.code] });
                                  } else {
                                    setNewEvent({ ...newEvent, divisions: currentDivisions.filter(d => d !== division.code) });
                                  }
                                }}
                                className="h-4 w-4 text-oldham-gold focus:ring-oldham-gold border-gray-300 rounded"
                              />
                              <span className="text-gray-700">
                                {division.name} <span className="text-xs text-gray-500">({division.age})</span>
                              </span>
                            </label>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Select which age divisions can participate in this event
                        </p>
                      </div>
                      
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={addEvent}
                          disabled={!selectedEventId || !newEvent.entry_fee}
                          className="bg-oldham-gold text-gray-900 px-4 py-2 rounded text-sm hover:bg-oldham-gold/90 disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                          Add Event
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Events List */}
                  {formData.events.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Added Events:</h4>
                      {formData.events.map((event, index) => (
                        <div key={index} className="flex items-center justify-between bg-white p-3 rounded-lg border">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                event.type === 'track' ? 'bg-blue-100 text-blue-800' : 
                                event.type === 'field' ? 'bg-green-100 text-green-800' : 
                                'bg-purple-100 text-purple-800'
                              }`}>
                                {event.type.toUpperCase()}
                              </span>
                              <span className="text-sm font-medium">
                                {toProperCase(event.event_name || event.event_code || '')} ({event.gender}) - ${event.entry_fee}
                              </span>
                            </div>
                            {event.divisions && event.divisions.length > 0 && (
                              <div className="flex flex-wrap gap-1 ml-0">
                                <span className="text-xs text-gray-500 mr-1">Divisions:</span>
                                {event.divisions.map((div, idx) => (
                                  <span key={idx} className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded text-xs">
                                    {div}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => removeEvent(index)}
                            className="text-red-600 hover:text-red-800 text-sm px-2 py-1 hover:bg-red-50 rounded ml-2"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-oldham-gold text-gray-900 rounded-md hover:bg-oldham-gold/90"
                  >
                    Create Competition
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Competition Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Competition</h3>
              
              <form onSubmit={handleUpdateCompetition} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Competition Name</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-oldham-gold focus:border-oldham-gold"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Location</label>
                    <input
                      type="text"
                      required
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-oldham-gold focus:border-oldham-gold"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-oldham-gold focus:border-oldham-gold"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Start Date</label>
                    <input
                      type="date"
                      required
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-oldham-gold focus:border-oldham-gold"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">End Date</label>
                    <input
                      type="date"
                      required
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-oldham-gold focus:border-oldham-gold"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Registration Deadline</label>
                    <input
                      type="date"
                      required
                      value={formData.registration_deadline}
                      onChange={(e) => setFormData({ ...formData, registration_deadline: e.target.value })}
                      className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-oldham-gold focus:border-oldham-gold"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Max Participants</label>
                  <input
                    type="number"
                    value={formData.max_participants}
                    onChange={(e) => setFormData({ ...formData, max_participants: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-oldham-gold focus:border-oldham-gold"
                  />
                </div>

                {/* Free Registration Toggle */}
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="edit_free_registration"
                    checked={formData.free_registration}
                    onChange={(e) => setFormData({ ...formData, free_registration: e.target.checked })}
                    className="h-4 w-4 text-oldham-gold focus:ring-oldham-gold border-gray-300 rounded"
                  />
                  <label htmlFor="edit_free_registration" className="ml-2 block text-sm font-medium text-gray-700">
                    Allow free registration (no entry fees)
                  </label>
                </div>

                {/* Events List (Read-only in edit mode) */}
                {formData.events && formData.events.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Events</label>
                    <div className="space-y-2">
                      {formData.events.map((event, index) => (
                        <div key={index} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                          <span className="text-sm">
                            {toProperCase(event.event_name || event.event_code || '')} ({event.gender}) - ${event.entry_fee}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-oldham-gold text-gray-900 rounded-md hover:bg-oldham-gold/90"
                  >
                    Update Competition
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Registrations Modal */}
      {showRegistrationsModal && selectedCompetition && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-4/5 lg:w-3/4 xl:w-2/3 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Registrations for {selectedCompetition.name}
                </h3>
                <button
                  onClick={() => setShowRegistrationsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {registrations.length === 0 ? (
                <div className="text-center py-8">
                  <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No registrations</h3>
                  <p className="mt-1 text-sm text-gray-500">No one has registered for this competition yet.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Events</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Seed Timing</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Registration Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {[...registrations]
                        .sort((a, b) => {
                          const nameA = `${(a.user_profile?.first_name || '')} ${(a.user_profile?.last_name || '')}`.trim().toLowerCase();
                          const nameB = `${(b.user_profile?.first_name || '')} ${(b.user_profile?.last_name || '')}`.trim().toLowerCase();
                          return nameA.localeCompare(nameB);
                        })
                        .flatMap((registration) => {
                          const events = registration.events?.length > 0
                            ? registration.events
                            : [{ event_name: '', event_code: '', seed_timing: '' }];
                          const rowCount = events.length;
                          const displayName = registration.user_profile
                            ? `${registration.user_profile.first_name || ''} ${registration.user_profile.last_name || ''}`.trim() || 'Unknown User'
                            : 'Unknown User';
                          const formatRegDate = (reg) => {
                            if (!reg.registration_date) return 'N/A';
                            try {
                              if (reg.registration_date.seconds) {
                                return new Date(reg.registration_date.seconds * 1000).toLocaleDateString();
                              }
                              if (typeof reg.registration_date === 'string') {
                                return new Date(reg.registration_date).toLocaleDateString();
                              }
                              if (reg.registration_date instanceof Date) {
                                return reg.registration_date.toLocaleDateString();
                              }
                              return 'N/A';
                            } catch {
                              return 'N/A';
                            }
                          };
                          const statusBadge = (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              registration.status === 'confirmed'
                                ? 'bg-green-100 text-green-800'
                                : registration.status === 'pending_payment'
                                ? 'bg-yellow-100 text-yellow-800'
                                : registration.status === 'rejected'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {registration.status === 'confirmed' ? 'Confirmed' :
                               registration.status === 'pending_payment' ? 'Pending Payment' :
                               registration.status === 'rejected' ? 'Rejected' :
                               registration.status || 'Unknown'}
                            </span>
                          );
                          const actionsCell = registration.status === 'rejected' || registration.status === 'confirmed' ? (
                            <span className="text-gray-400 text-xs">No actions</span>
                          ) : (
                            <span className="inline-flex items-center gap-2">
                              <button
                                onClick={() => handleApproveRegistration(registration.id, displayName)}
                                className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                                title="Approve registration without payment"
                              >
                                <CheckCircleIcon className="h-4 w-4 mr-1" />
                                Approve
                              </button>
                              <button
                                onClick={() => handleRejectRegistration(registration.id, displayName)}
                                className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                                title="Reject registration"
                              >
                                <XMarkIcon className="h-4 w-4 mr-1" />
                                Reject
                              </button>
                            </span>
                          );
                          return events.map((event, eventIndex) => (
                            <tr key={`${registration.id}-${eventIndex}`}>
                              {eventIndex === 0 && (
                                <>
                                  <td rowSpan={rowCount} className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-b border-gray-200 align-top">
                                    {displayName}
                                  </td>
                                  <td rowSpan={rowCount} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border-b border-gray-200 align-top">
                                    {registration.user_profile?.email || 'N/A'}
                                  </td>
                                </>
                              )}
                              <td className="px-6 py-4 text-sm text-gray-500 border-b border-gray-200">
                                {toProperCase(event.event_name || event.event || '') || 'N/A'}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500 border-b border-gray-200">
                                {event.seed_timing || 'N/A'}
                              </td>
                              {eventIndex === 0 && (
                                <>
                                  <td rowSpan={rowCount} className="px-6 py-4 whitespace-nowrap border-b border-gray-200 align-top">
                                    {statusBadge}
                                  </td>
                                  <td rowSpan={rowCount} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border-b border-gray-200 align-top">
                                    {formatRegDate(registration)}
                                  </td>
                                  <td rowSpan={rowCount} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border-b border-gray-200 align-top">
                                    {actionsCell}
                                  </td>
                                </>
                              )}
                            </tr>
                          ));
                        })}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="flex justify-end pt-4">
                <button
                  onClick={() => setShowRegistrationsModal(false)}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCompetitions;
